/**
 * InlineScript tool processor for the Now Assist Skill Plugin.
 * Handles processing of InlineScript tools defined in NowAssistSkillConfig.
 */

import { TOOL_TYPES, TOOL_ATTRIBUTE_IDS } from '../defaults'
import { type Context, type ObjectShape, type Record as BuildRecord, Shape } from '@servicenow/sdk-build-core'
import { applyDefaults, buildDependsOnList, createDependencyEdges } from '../skill-plugin-helpers'
import type { ToolDefinition } from '../tool-types'
import {
    SYS_ONE_EXTEND_RESOURCE_MAPPING,
    SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
    getResourceCapability,
    getApplicabilityProperties,
} from '../tool-utils'

/**
 * Process an InlineScript tool and create the necessary records
 *
 * InlineScript tools create:
 * 1. sys_one_extend_resource_mapping - The main tool record
 * 2. sys_one_extend_resource_attribute_mapping - Output attribute mapping (fixed: Output, type: object)
 * 3. sys_one_extend_resource_edge - Dependency edges (if depends is specified)
 */
export async function processInlineScriptTool(
    tool: ToolDefinition,
    capabilityRecord: BuildRecord,
    toolRecordsMap: Map<string, BuildRecord>,
    arg: ObjectShape,
    factory: Context['factory'],
    diagnostics: Context['diagnostics'],
    decisionToolNames?: Set<string>
): Promise<BuildRecord[]> {
    const records: BuildRecord[] = []

    // Validate tool type
    if (tool.type !== TOOL_TYPES.INLINE_SCRIPT) {
        diagnostics.error(arg, `Expected inline-script tool but got ${tool.type}`)
        return records
    }

    // Validate required fields
    if (!tool.name) {
        diagnostics.error(arg, 'InlineScript tool requires a name')
        return records
    }

    // Build depends_on_list from dependency names
    const dependsOnList = buildDependsOnList(tool, toolRecordsMap, arg, diagnostics, decisionToolNames)

    // 1. Create sys_one_extend_resource_mapping record
    const resourceMappingRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_ONE_EXTEND_RESOURCE_MAPPING,
        ...(tool.$id ? { explicitId: tool.$id } : {}),
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_MAPPING, $),
            resource_name: $.val(tool.name),
            parent_capability: $.val(capabilityRecord.getId()),
            resource_capability: $.val(getResourceCapability(tool.type)),
            ...getApplicabilityProperties(tool.condition, $),
            depends_on_list: $.val(dependsOnList),
        })),
    })
    records.push(resourceMappingRecord)

    // Store in map for dependency resolution
    toolRecordsMap.set(tool.name, resourceMappingRecord)

    // 2. Create sys_one_extend_resource_attribute_mapping for input (the script body)
    // InlineScript stores the script as an input attribute with resource_attribute_name = 'inputscript' sys_id
    const scriptContent = tool.script

    // Wrap the script in the standard function wrapper if it's not already wrapped
    const wrappedScript = scriptContent?.startsWith('(function')
        ? scriptContent
        : `(function runScript(context) {\n    ${scriptContent}\n})(context);`

    const inputAttributeRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING, $),
            resource_mapping: $.val(resourceMappingRecord.getId()),
            resource_attribute_name: $.val(TOOL_ATTRIBUTE_IDS.INLINE_SCRIPT_INPUT),
            attribute_value_script: $.val(wrappedScript).toCdata(),
            attribute_value_template: $.val(wrappedScript).toCdata(),
            type: $.val('input'),
        })),
    })
    records.push(inputAttributeRecord)

    // 3. Create sys_one_extend_resource_attribute_mapping for output
    // InlineScript has fixed output: { name: 'Output', type: 'object' }
    const outputAttributeRecord = await factory.createRecord({
        source: tool._source!,
        table: SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING,
        properties: arg.transform(({ $ }) => ({
            ...applyDefaults(SYS_ONE_EXTEND_RESOURCE_ATTRIBUTE_MAPPING, $),
            resource_attribute_name: $.val(TOOL_ATTRIBUTE_IDS.INLINE_SCRIPT_OUTPUT),
            resource_mapping: $.val(resourceMappingRecord.getId()),
            truncate: $.val(tool.truncate ?? false),
            type: $.val('output'),
        })),
    })
    records.push(outputAttributeRecord)

    // 4. Create sys_one_extend_resource_edge records for dependencies
    // InlineScript edges do not include applicability/error handler scripts
    const edgeRecords = await createDependencyEdges(
        tool,
        toolRecordsMap,
        resourceMappingRecord,
        arg,
        factory,
        false,
        tool._source!
    )
    records.push(...edgeRecords)

    return records
}
